import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Trash2, Loader2, Images, Package } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { id: "all", label: "Everything" },
  { id: "image", label: "Images" },
  { id: "logo", label: "Logos" },
  { id: "tattoo", label: "Tattoos" },
];

const KIND_LABEL = { image: "Image", logo: "Logo", tattoo: "Tattoo" };

const CreationCard = ({ item, onDelete }) => {
  const [url, setUrl] = useState(null);
  const [packing, setPacking] = useState(false);

  useEffect(() => {
    let objectUrl;
    let cancelled = false;
    api
      .get(`/creations/${item.creation_id}/file`, { responseType: "blob" })
      .then(({ data }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.creation_id]);

  const downloadPack = async () => {
    setPacking(true);
    try {
      const { data } = await api.get(`/creations/${item.creation_id}/pack`, { responseType: "blob" });
      const href = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = href;
      a.download = `${item.creation_id}-pack.zip`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not build the pack");
    } finally {
      setPacking(false);
    }
  };

  return (
    <div
      data-testid={`creation-${item.creation_id}`}
      className="group overflow-hidden rounded-3xl border border-white/10 bg-panel transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40"
    >
      <div className="flex aspect-square items-center justify-center bg-page">
        {url ? (
          <img src={url} alt={item.title || item.prompt} className="h-full w-full object-cover" />
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        )}
      </div>
      <div className="space-y-3 p-4">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-brand/15 px-2.5 py-1 text-xs font-semibold text-brand">
            {KIND_LABEL[item.kind] || item.kind}
          </span>
          <span className="text-xs text-ink-soft">{(item.created_at || "").slice(0, 10)}</span>
        </div>
        <p dir="auto" className="line-clamp-2 text-sm text-ink-soft">{item.title || item.prompt}</p>
        <div className="flex flex-wrap items-center gap-2">
          {url && (
            <a
              data-testid={`creation-download-${item.creation_id}`}
              href={url}
              download={`chatmilu-${item.kind}-${item.creation_id}.png`}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs hover:bg-white/20"
            >
              <Download className="h-3.5 w-3.5" /> PNG
            </a>
          )}
          {item.kind === "logo" && (
            <button
              data-testid={`creation-pack-${item.creation_id}`}
              onClick={downloadPack}
              disabled={packing}
              className="inline-flex items-center gap-2 rounded-full bg-brand/15 px-4 py-2 text-xs text-brand hover:bg-brand/25"
            >
              {packing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Package className="h-3.5 w-3.5" />}
              Logo pack
            </button>
          )}
          <button
            data-testid={`creation-delete-${item.creation_id}`}
            onClick={() => onDelete(item)}
            className="ms-auto text-ink-soft transition-colors hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Creations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/creations");
      setItems(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (item) => {
    setItems((list) => list.filter((i) => i.creation_id !== item.creation_id));
    try {
      await api.delete(`/creations/${item.creation_id}`);
      toast.success("Deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      load();
    }
  };

  const shown = filter === "all" ? items : items.filter((i) => i.kind === filter);

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8" data-testid="creations-page">
      <Link to="/chat" data-testid="back-to-chat" className="inline-flex items-center gap-2 text-sm text-ink-soft hover:text-brand">
        <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> Back to AI Chat
      </Link>
      <div className="mt-6 flex items-start gap-4">
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/15 text-brand">
          <Images className="h-6 w-6" />
        </span>
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">My Creations</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-soft md:text-base">
            Every image, logo and tattoo you generated — revisit, re-download or clear them out.
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="creation-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`creation-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`min-h-[40px] rounded-full px-5 text-sm transition-colors ${
              filter === f.id ? "bg-brand text-white" : "bg-surfacealt text-ink-soft hover:text-ink"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading && <Loader2 className="mt-10 h-6 w-6 animate-spin text-brand" data-testid="creations-loading" />}

      {!loading && shown.length === 0 && (
        <div
          data-testid="creations-empty"
          className="mt-10 rounded-3xl border border-white/10 bg-panel p-10 text-center"
        >
          <p className="text-sm text-ink-soft">
            Nothing here yet. Generate an image, logo or tattoo from Elite Tools and it lands in this gallery.
          </p>
          <Button asChild className="mt-6 min-h-[44px] rounded-full bg-brand px-6 hover:bg-brand-hover">
            <Link to="/chat?persona=elite" data-testid="creations-go-elite">Open Elite Tools</Link>
          </Button>
        </div>
      )}

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="creations-grid">
        {shown.map((item) => (
          <CreationCard key={item.creation_id} item={item} onDelete={remove} />
        ))}
      </div>
    </div>
  );
}
